/**
 * Products Service — all product-related API calls.
 *
 * Features:
 * - In-memory cache with TTL
 * - NO mock fallback — if API fails or returns empty, returns empty data
 * - Consistent error handling
 * - Typed responses
 * - Catálogo público de ChekList (Google Sheets)
 */

import { api, CATALOGO_URL } from './api';
import {
  normalizeProducts,
  groupProductsByCategory,
  categoryNameOf,
} from './api-adapter';
import type { CategoryGroup } from './api-adapter';
import { cache, cacheKeys, TTL } from './cache';
import type { Product, ProductFilters, PaginatedResponse } from '@/types';
import { DEFAULT_PAGE_SIZE } from '@/utils/constants';
import { ofertasPrimero } from '@/utils/helpers';

export type { CategoryGroup };

// ─── Client-side filter helper ────────────────────────────────────────────────
function applyClientFilters(list: Product[], filters: ProductFilters): Product[] {
  let result = [...list];

  if (filters.categoryId && filters.categoryId !== 'all') {
    result = result.filter((p) => p.categoryId === filters.categoryId);
  }
  if (filters.subcategoryId) {
    result = result.filter((p) => p.subcategoryId === filters.subcategoryId);
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }
  if (filters.inStock !== undefined) {
    result = result.filter((p) => p.inStock === filters.inStock);
  }
  if (filters.minPrice !== undefined) {
    result = result.filter((p) => p.price >= (filters.minPrice ?? 0));
  }
  if (filters.maxPrice !== undefined) {
    result = result.filter((p) => p.price <= (filters.maxPrice ?? Infinity));
  }
  if (filters.brand) {
    result = result.filter(
      (p) => p.brand?.toLowerCase() === filters.brand?.toLowerCase(),
    );
  }
  if (filters.tags && filters.tags.length > 0) {
    result = result.filter((p) =>
      filters.tags!.some((tag) => p.tags.includes(tag)),
    );
  }

  switch (filters.sortBy) {
    case 'price-asc':
      result.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      result.sort((a, b) => b.price - a.price);
      break;
    case 'rating':
      result.sort((a, b) => b.rating - a.rating);
      break;
    case 'newest':
      result.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      break;
    case 'name-asc':
      result.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'name-desc':
      result.sort((a, b) => b.name.localeCompare(a.name));
      break;
    case 'discount':
      result.sort((a, b) => (b.discount ?? 0) - (a.discount ?? 0));
      break;
    default:
      // Sin orden elegido: las ofertas primero
      result = ofertasPrimero(result);
      break;
  }

  return result;
}

// ─── getAllProducts (fetches full catalog) ────────────────────────────────────
/**
 * Fetches ALL products from the API (no pagination on the API side).
 * ChekList devuelve el catálogo completo: se normaliza y se guarda en caché.
 * Returns empty array if API fails or returns no products.
 */
export async function getAllProducts(): Promise<Product[]> {
  const cacheKey = 'all-products';
  const cached = cache.get<Product[]>(cacheKey);
  if (cached) return cached;

  const raw = await api.get<unknown>(CATALOGO_URL);
  const products = normalizeProducts(raw);

  cache.set(cacheKey, products, TTL.SHORT);
  return products;
}

// ─── getProducts ──────────────────────────────────────────────────────────────
export async function getProducts(
  filters: ProductFilters = {},
): Promise<PaginatedResponse<Product>> {
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
  const cacheKey = cacheKeys.products({ ...filters, page, pageSize });

  const cached = cache.get<PaginatedResponse<Product>>(cacheKey);
  if (cached) return cached;

  // Fetch all products and filter client-side
  const allProducts = await getAllProducts();
  const filtered = applyClientFilters(allProducts, filters);
  const start = (page - 1) * pageSize;
  const sliced = filtered.slice(start, start + pageSize);
  const total = filtered.length;
  const totalPages = Math.ceil(total / pageSize);

  const result: PaginatedResponse<Product> = {
    data: sliced,
    total,
    page,
    pageSize,
    totalPages,
    hasNextPage: page < totalPages,
    hasPrevPage: page > 1,
  };

  cache.set(cacheKey, result, TTL.SHORT);
  return result;
}

// ─── getProductsGroupedByCategory ────────────────────────────────────────────
/**
 * Returns all products grouped by category.
 * Used for the Home page Airbnb-style layout.
 */
export async function getProductsGroupedByCategory(): Promise<CategoryGroup[]> {
  const cacheKey = 'products-by-category';
  const cached = cache.get<CategoryGroup[]>(cacheKey);
  if (cached) return cached;

  const allProducts = await getAllProducts();
  const result = groupProductsByCategory(allProducts);

  cache.set(cacheKey, result, TTL.SHORT);
  return result;
}

// ─── getCategoriesFromAPI ─────────────────────────────────────────────────────
/**
 * Extracts categories from the API metadata object.
 * Returns Category objects derived from the real API data.
 */
export async function getCategoriesFromAPI() {
  const cacheKey = 'api-categories';
  const cached = cache.get<{ id: string; name: string; count: number }[]>(cacheKey);
  if (cached) return cached;

  const allProducts = await getAllProducts();

  const countMap = new Map<string, { name: string; count: number }>();
  allProducts.forEach((p) => {
    const existing = countMap.get(p.categoryId);
    if (existing) {
      existing.count += 1;
    } else {
      countMap.set(p.categoryId, { name: categoryNameOf(p), count: 1 });
    }
  });

  const result = Array.from(countMap.entries()).map(([id, { name, count }]) => ({
    id,
    name,
    count,
  }));

  cache.set(cacheKey, result, TTL.SHORT);
  return result;
}

// ─── getProductById ───────────────────────────────────────────────────────────
export async function getProductById(id: string): Promise<Product | null> {
  const cacheKey = cacheKeys.product(id);
  const cached = cache.get<Product>(cacheKey);
  if (cached) return cached;

  const allProducts = await getAllProducts();
  const found = allProducts.find((p) => p.id === id || p.slug === id) ?? null;
  if (found) cache.set(cacheKey, found, TTL.SHORT);
  return found;
}

// ─── getFeaturedProducts ──────────────────────────────────────────────────────
export async function getFeaturedProducts(limit = 4): Promise<Product[]> {
  const cacheKey = cacheKeys.featured();
  const cached = cache.get<Product[]>(cacheKey);
  if (cached) return cached;

  const allProducts = await getAllProducts();
  // Destacados: los que tienen etiqueta; si no hay, los primeros con stock
  let featured = allProducts.filter((p) => p.badge).slice(0, limit);
  if (featured.length === 0) {
    const conStock = allProducts.filter((p) => p.inStock);
    featured = (conStock.length > 0 ? conStock : allProducts).slice(0, limit);
  }

  cache.set(cacheKey, featured, TTL.SHORT);
  return featured;
}

// ─── getRelatedProducts ───────────────────────────────────────────────────────
export async function getRelatedProducts(
  productId: string,
  categoryId: string,
  limit = 4,
): Promise<Product[]> {
  const cacheKey = cacheKeys.related(productId, categoryId);
  const cached = cache.get<Product[]>(cacheKey);
  if (cached) return cached;

  const allProducts = await getAllProducts();
  const result = allProducts
    .filter((p) => p.categoryId === categoryId && p.id !== productId)
    .slice(0, limit);

  cache.set(cacheKey, result, TTL.SHORT);
  return result;
}

// ─── searchProducts ───────────────────────────────────────────────────────────
export async function searchProducts(
  query: string,
  limit = 10,
): Promise<Product[]> {
  if (!query.trim()) return [];

  const cacheKey = `search:${query.toLowerCase().trim()}:${limit}`;
  const cached = cache.get<Product[]>(cacheKey);
  if (cached) return cached;

  const allProducts = await getAllProducts();
  const q = query.toLowerCase();
  const result = allProducts
    .filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q)) ||
        p.categoryId.includes(q),
    )
    .slice(0, limit);

  cache.set(cacheKey, result, TTL.SHORT);
  return result;
}

// ─── getProductsByCategory ────────────────────────────────────────────────────
export async function getProductsByCategory(
  categoryId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
): Promise<PaginatedResponse<Product>> {
  return getProducts({ categoryId, page, pageSize });
}

// ─── invalidateProductCache ───────────────────────────────────────────────────
export function invalidateProductCache(): void {
  cache.invalidatePrefix('products:');
  cache.invalidatePrefix('product:');
  cache.invalidatePrefix('search:');
  cache.invalidatePrefix('all-products');
  cache.invalidatePrefix('products-by-category');
  cache.invalidatePrefix('api-categories');
}
